import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import MenuPreferencesFields from './MenuPreferencesFields';
import { DEFAULT_MENU_PREFERENCES } from './menuPreferencesConstants';
import { setCachedWeightUnit } from '../hooks/useWeightUnit';
import {
  bodyWeightInputBounds,
  convertDisplayWeight,
  displayWeightToLbs,
  lbsToDisplayWeight,
  resolveWeightUnit
} from '../utils/weightUnits';
import './UserProfile.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const FITNESS_GOALS = [
  'Lose weight', 'Build muscle', 'Improve flexibility', 'Increase endurance',
  'Reduce pain', 'Improve posture', 'General fitness', 'Sport performance',
  'Stress relief', 'Rehabilitation'
];
const EQUIPMENT_OPTIONS = [
  'Dumbbells', 'Barbell', 'Kettlebell', 'Resistance bands', 'Pull-up bar',
  'Bench', 'Cable machine', 'Treadmill', 'Stationary bike', 'Rowing machine',
  'Yoga mat', 'Foam roller', 'Medicine ball', 'TRX / Suspension trainer',
  'None / Bodyweight only'
];
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const UserProfile = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [form, setForm] = useState({
    age: '', gender: '', height: '', currentWeight: '', goalWeight: '',
    activityLevel: '', fitnessLevel: '', fitnessGoals: [], preferredWorkoutDays: [],
    sessionMinutes: '', hasGymAccess: false, availableEquipment: [], injuries: '',
    bio: '', weightUnit: 'lb'
  });
  const [menuPreferences, setMenuPreferences] = useState(DEFAULT_MENU_PREFERENCES);
  const [sendingSample, setSendingSample] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load profile');
      const data = await res.json();
      setProfileData(data);

      const p = data.profile || {};
      const weightUnit = resolveWeightUnit(data.preferences?.weightUnit);
      setCachedWeightUnit(weightUnit);
      setForm({
        age: p.age || '',
        gender: p.gender || '',
        height: p.height || '',
        currentWeight: lbsToDisplayWeight(p.currentWeight, weightUnit).value,
        goalWeight: lbsToDisplayWeight(p.goalWeight, weightUnit).value,
        activityLevel: p.activityLevel || '',
        fitnessLevel: p.fitnessLevel || '',
        fitnessGoals: p.fitnessGoals || [],
        preferredWorkoutDays: p.preferredWorkoutDays || [],
        sessionMinutes: p.sessionMinutes || '',
        hasGymAccess: p.hasGymAccess || false,
        availableEquipment: p.availableEquipment || [],
        injuries: (p.injuries || []).join(', '),
        bio: p.bio || '',
        weightUnit
      });

      try {
        const prefRes = await fetch(`${API_URL}/mealplan/preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (prefRes.ok) {
          const prefData = await prefRes.json();
          setMenuPreferences({
            ...DEFAULT_MENU_PREFERENCES,
            ...(prefData.preferences || {})
          });
        }
      } catch (prefErr) {
        console.error('Failed to load menu preferences', prefErr);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayItem = (field, item) => {
    setForm(prev => {
      const arr = prev[field];
      return { ...prev, [field]: arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item] };
    });
  };

  const splitTrim = (str) => str ? str.split(',').map(s => s.trim()).filter(Boolean) : [];

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const body = {
        profile: {
          age: form.age ? Number(form.age) : undefined,
          gender: form.gender || undefined,
          height: form.height ? Number(form.height) : undefined,
          currentWeight: displayWeightToLbs(form.currentWeight, form.weightUnit),
          goalWeight: displayWeightToLbs(form.goalWeight, form.weightUnit),
          activityLevel: form.activityLevel || undefined,
          fitnessLevel: form.fitnessLevel || undefined,
          fitnessGoals: form.fitnessGoals,
          preferredWorkoutDays: form.preferredWorkoutDays,
          sessionMinutes: form.sessionMinutes ? Number(form.sessionMinutes) : undefined,
          hasGymAccess: form.hasGymAccess,
          availableEquipment: form.availableEquipment,
          injuries: splitTrim(form.injuries),
          bio: form.bio
        },
        preferences: {
          weightUnit: resolveWeightUnit(form.weightUnit)
        }
      };

      const res = await fetch(`${API_URL}/profile`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!res.ok) throw new Error('Failed to save profile');
      const data = await res.json();
      setProfileData(data);
      setCachedWeightUnit(data.preferences?.weightUnit || form.weightUnit);

      const prefRes = await fetch(`${API_URL}/mealplan/preferences`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          preferences: {
            ...menuPreferences,
            onboardingCompleted: true
          }
        })
      });
      if (!prefRes.ok) throw new Error('Failed to save menu preferences');
      const prefData = await prefRes.json();
      if (prefData.preferences) {
        setMenuPreferences({ ...DEFAULT_MENU_PREFERENCES, ...prefData.preferences });
      }

      toast.success('Profile saved!');
    } catch (err) {
      console.error(err);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="user-profile-page"><div className="profile-loading">Loading profile...</div></div>;
  }

  return (
    <div className="user-profile-page">
      <div className="profile-header">
        <div className="profile-avatar">
          {profileData?.picture ? (
            <img src={profileData.picture} alt={profileData.name} referrerPolicy="no-referrer" />
          ) : (
            <div className="avatar-placeholder">{profileData?.name?.[0]?.toUpperCase() || '?'}</div>
          )}
        </div>
        <div className="profile-header-info">
          <h1>{profileData?.name}</h1>
          <p className="profile-email">{profileData?.email}</p>
          <span className={`profile-tier-badge tier-${profileData?.tier}`}>
            {profileData?.tier?.charAt(0).toUpperCase() + profileData?.tier?.slice(1)} Plan
          </span>
        </div>
      </div>

      <form onSubmit={handleSave} className="profile-form">
        {/* About */}
        <section className="profile-section">
          <h2>About You</h2>
          <div className="profile-field">
            <label>Bio</label>
            <textarea
              value={form.bio}
              onChange={e => handleChange('bio', e.target.value)}
              placeholder="Tell your AI coach a little about yourself..."
              maxLength={500}
              rows={3}
            />
          </div>
          <div className="profile-row">
            <div className="profile-field">
              <label>Age</label>
              <input type="number" value={form.age} onChange={e => handleChange('age', e.target.value)} min="13" max="120" placeholder="e.g. 30" />
            </div>
            <div className="profile-field">
              <label>Gender</label>
              <select value={form.gender} onChange={e => handleChange('gender', e.target.value)}>
                <option value="">Select...</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <div className="profile-row">
            <div className="profile-field">
              <label>Units</label>
              <select
                value={form.weightUnit}
                onChange={e => {
                  const next = resolveWeightUnit(e.target.value);
                  setForm(prev => ({
                    ...prev,
                    weightUnit: next,
                    currentWeight: convertDisplayWeight(prev.currentWeight, prev.weightUnit, next),
                    goalWeight: convertDisplayWeight(prev.goalWeight, prev.weightUnit, next)
                  }));
                  setCachedWeightUnit(next);
                }}
              >
                <option value="lb">Pounds (lb)</option>
                <option value="kg">Kilograms (kg)</option>
              </select>
            </div>
            <div className="profile-field">
              <label>Height (inches)</label>
              <input type="number" value={form.height} onChange={e => handleChange('height', e.target.value)} min="36" max="96" placeholder="e.g. 68" />
            </div>
          </div>
          <div className="profile-row">
            <div className="profile-field">
              <label>Current Weight ({form.weightUnit})</label>
              <input
                type="number"
                step="0.1"
                value={form.currentWeight}
                onChange={e => handleChange('currentWeight', e.target.value)}
                min={bodyWeightInputBounds(form.weightUnit).min}
                max={bodyWeightInputBounds(form.weightUnit).max}
                placeholder={bodyWeightInputBounds(form.weightUnit).placeholder}
              />
            </div>
            <div className="profile-field">
              <label>Goal Weight ({form.weightUnit})</label>
              <input
                type="number"
                step="0.1"
                value={form.goalWeight}
                onChange={e => handleChange('goalWeight', e.target.value)}
                min={bodyWeightInputBounds(form.weightUnit).min}
                max={bodyWeightInputBounds(form.weightUnit).max}
                placeholder={form.weightUnit === 'kg' ? 'e.g. 73' : 'e.g. 160'}
              />
            </div>
          </div>
          <div className="profile-row">
            <div className="profile-field">
              <label>Activity Level</label>
              <select value={form.activityLevel} onChange={e => handleChange('activityLevel', e.target.value)}>
                <option value="">Select...</option>
                <option value="sedentary">Sedentary (little/no exercise)</option>
                <option value="light">Light (1-3 days/week)</option>
                <option value="moderate">Moderate (3-5 days/week)</option>
                <option value="active">Active (6-7 days/week)</option>
                <option value="very_active">Very Active (2x/day or physical job)</option>
              </select>
            </div>
          </div>
        </section>

        {/* Fitness */}
        <section className="profile-section">
          <h2>Fitness</h2>
          <div className="profile-field">
            <label>Fitness Level</label>
            <select value={form.fitnessLevel} onChange={e => handleChange('fitnessLevel', e.target.value)}>
              <option value="">Select...</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>
          <div className="profile-field">
            <label>Fitness Goals</label>
            <div className="chip-grid">
              {FITNESS_GOALS.map(goal => (
                <button
                  key={goal}
                  type="button"
                  className={`chip ${form.fitnessGoals.includes(goal) ? 'selected' : ''}`}
                  onClick={() => toggleArrayItem('fitnessGoals', goal)}
                >
                  {goal}
                </button>
              ))}
            </div>
          </div>
          <div className="profile-field">
            <label>Preferred Workout Days</label>
            <div className="chip-grid">
              {DAYS.map(day => (
                <button
                  key={day}
                  type="button"
                  className={`chip ${form.preferredWorkoutDays.includes(day) ? 'selected' : ''}`}
                  onClick={() => toggleArrayItem('preferredWorkoutDays', day)}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>
          <div className="profile-row">
            <div className="profile-field">
              <label>Session Duration (minutes)</label>
              <input type="number" value={form.sessionMinutes} onChange={e => handleChange('sessionMinutes', e.target.value)} min="10" max="180" placeholder="e.g. 45" />
            </div>
            <div className="profile-field checkbox-field">
              <label>
                <input type="checkbox" checked={form.hasGymAccess} onChange={e => handleChange('hasGymAccess', e.target.checked)} />
                I have gym access
              </label>
            </div>
          </div>
          <div className="profile-field">
            <label>Available Equipment</label>
            <div className="chip-grid">
              {EQUIPMENT_OPTIONS.map(eq => (
                <button
                  key={eq}
                  type="button"
                  className={`chip ${form.availableEquipment.includes(eq) ? 'selected' : ''}`}
                  onClick={() => toggleArrayItem('availableEquipment', eq)}
                >
                  {eq}
                </button>
              ))}
            </div>
          </div>
          <div className="profile-field">
            <label>Injuries / Limitations</label>
            <input
              type="text"
              value={form.injuries}
              onChange={e => handleChange('injuries', e.target.value)}
              placeholder="e.g. lower back pain, bad left knee (comma-separated)"
            />
          </div>
        </section>

        {/* Menu preferences */}
        <section className="profile-section">
          <h2>Menu preferences</h2>
          <p className="profile-section-copy">
            These preferences personalize MealPlanify and weekly recipe emails. You can change them anytime.
          </p>
          <MenuPreferencesFields
            preferences={menuPreferences}
            onChange={(field, value) => setMenuPreferences((prev) => ({ ...prev, [field]: value }))}
          />
          <div className="profile-sample-email">
            <button
              type="button"
              className="profile-secondary-btn"
              disabled={sendingSample}
              onClick={async () => {
                setSendingSample(true);
                try {
                  const token = localStorage.getItem('token');
                  const res = await fetch(`${API_URL}/mealplan/weekly-email/send-test`, {
                    method: 'POST',
                    headers: {
                      Authorization: `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ preferences: menuPreferences })
                  });
                  const data = await res.json();
                  if (!res.ok) throw new Error(data.error || 'Failed to send sample');
                  if (data.sent) {
                    toast.success(`Sample meal plan emailed to ${data.to}`);
                  } else {
                    toast.success('Preview generated. Email sending is not configured on this environment.');
                    const preview = window.open('', '_blank');
                    if (preview) {
                      preview.document.write(data.html || '<p>No preview available</p>');
                      preview.document.close();
                    }
                  }
                } catch (err) {
                  console.error(err);
                  toast.error(err.message || 'Failed to send sample email');
                } finally {
                  setSendingSample(false);
                }
              }}
            >
              {sendingSample ? 'Generating sample…' : 'Send me a sample weekly meal plan'}
            </button>
          </div>
        </section>

        <div className="profile-actions">
          <button type="submit" className="profile-save-btn" disabled={saving}>
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserProfile;
